import { useState, useEffect, useRef } from 'react';

const SPINNER_DELAY = 100;
const MIN_SPINNER_DURATION = 300;

// useDeferredLoading is a pure visibility-timing wrapper around an
// external `isLoading` signal. It does not render a spinner or
// control data readiness — it only decides when a loading indicator
// should appear and disappear, adding a short delay before showing it
// and enforcing a minimum display duration once visible. This avoids
// distracting flashes on fast loads and keeps the timing logic out of
// every component that fetches data before rendering.
function useDeferredLoading(isLoading: boolean): {
	showSpinner: boolean;
	showContent: boolean;
} {
	const [showSpinner, setShowSpinner] = useState(false);
	const [showContent, setShowContent] = useState(!isLoading);

	const spinnerShownAtRef = useRef<number | null>(null);
	const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const holdTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (isLoading) {
			setShowContent(false);
			setShowSpinner(false);
			spinnerShownAtRef.current = null;

			showTimerRef.current = setTimeout(() => {
				setShowSpinner(true);
				spinnerShownAtRef.current = Date.now();
			}, SPINNER_DELAY);
		} else {
			// Loading finished.
			if (showTimerRef.current != null) {
				clearTimeout(showTimerRef.current);
				showTimerRef.current = null;
			}

			if (spinnerShownAtRef.current != null) {
				// Spinner was shown — hold it for the minimum duration.
				const elapsed = Date.now() - spinnerShownAtRef.current;
				const remaining = MIN_SPINNER_DURATION - elapsed;
				if (remaining > 0) {
					holdTimerRef.current = setTimeout(() => {
						setShowSpinner(false);
						setShowContent(true);
						spinnerShownAtRef.current = null;
					}, remaining);
				} else {
					setShowSpinner(false);
					setShowContent(true);
					spinnerShownAtRef.current = null;
				}
			} else {
				// Spinner was never shown — show content immediately.
				setShowSpinner(false);
				setShowContent(true);
			}
		}

		return () => {
			if (showTimerRef.current != null) {
				clearTimeout(showTimerRef.current);
				showTimerRef.current = null;
			}
			if (holdTimerRef.current != null) {
				clearTimeout(holdTimerRef.current);
				holdTimerRef.current = null;
			}
		};
	}, [isLoading]);

	return { showSpinner, showContent };
}

export { useDeferredLoading };
